import type React from 'react'
import type { ResultPill } from '../../components/v2/pv2-shared'
import { getVideoSession, deleteUserVideo, deleteUserVideoSession, getStreamRecordingUrl } from '../../lib/api'
import type { UserVideo } from '../../lib/api'
import {
  trackVideoDeleted,
  trackVideoSessionCleared,
  trackStreamHistoryViewed,
  trackDemoVideoSelected,
  trackVideoLibraryTabSwitched,
} from '../../lib/analytics'
import type { NarrativeEntry } from '../../lib/narrative'
import type { PageState } from '../../hooks/types'

/**
 * useVideoHandlers — Phase 5 (PRI-291)
 *
 * Extracts the 7 video library handler functions from PrimateVisionV2Inner:
 *   handleSelectUserVideo, handleDeleteUserVideo, handleDeleteDemoVideoSession,
 *   handleDeleteLiveStreamSessions, handleSelectDemoVideo,
 *   handleSelectLiveStreams, handleLiveStreamsNewSession
 *
 * Note: this hook is called from PrimateVisionV2Inner (outside V2Providers), so
 * context values are passed in directly rather than via context hooks.
 */
export function useVideoHandlers({
  // Auth
  fetchToken: _fetchToken,
  // Video library state (from useVideoLibrary)
  savedUserVideos,
  demoVideos,
  streamUserVideos,
  setSavedUserVideos,
  setSelectedSavedVideo,
  setSelectedDemoVideo,
  setSelectedFile,
  selectedSavedVideo,
  selectedDemoVideo,
  // Local state that isn't in contexts
  demoVideoUserVideoIds,
  setDemoVideoUserVideoIds,
  setSidebarOpen,
  // Stream state
  setResultPills,
  setIsStreaming,
  requestCameraAccess,
  lastStreamResultRef,
  // Callbacks into other local functions (used by handlers that call them)
  attachRenderedVideo,
  attachStreamRecordingUrl,
  setPageStateSync,
  // Analytics context (for trackDemoVideoSelected)
  prompt = '',
  userPlan = 'free',
  isAuthenticated = false,
}: {
  fetchToken: () => Promise<string | null>
  savedUserVideos: UserVideo[]
  demoVideos: Array<{ id: string; display_name: string; filename: string; thumbnail_url?: string | null }>
  streamUserVideos: UserVideo[]
  setSavedUserVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>
  setSelectedSavedVideo: (v: UserVideo | null) => void
  setSelectedDemoVideo: (v: string | null) => void
  setSelectedFile: (f: File | null) => void
  selectedSavedVideo: UserVideo | null
  selectedDemoVideo: string | null
  demoVideoUserVideoIds: Record<string, string>
  setDemoVideoUserVideoIds: React.Dispatch<React.SetStateAction<Record<string, string>>>
  setSidebarOpen: (open: boolean) => void
  setResultPills: React.Dispatch<React.SetStateAction<ResultPill[]>>
  setIsStreaming: (v: boolean) => void
  requestCameraAccess: () => Promise<void>
  lastStreamResultRef: React.MutableRefObject<unknown>
  attachRenderedVideo: (jobId: string) => Promise<void>
  attachStreamRecordingUrl: (pillId: string, recordingUrl: string) => Promise<void>
  setPageStateSync: (s: PageState) => void
  prompt?: string
  userPlan?: string
  isAuthenticated?: boolean
}) {
  // ── Shared helper: convert session DB entries to ResultPill array ──────────
  function sessionEntriesToPills(
    entries: Awaited<ReturnType<typeof getVideoSession>>['session'],
    userVideoId?: string,
  ): ResultPill[] {
    return entries.map(entry => {
      if (entry.query_type === 'stream') {
        return {
          id: entry.job_id?.startsWith('stream-') ? entry.job_id : 'stream-' + (entry.job_id ?? entry.id),
          prompt: entry.prompt,
          answer: (entry.answer?.toUpperCase() === 'YES' ? 'YES' : 'NO') as 'YES' | 'NO',
          confidence: Math.round((entry.confidence ?? 0) * 100),
          resultText: '',
          videoThumb: null,
          videoUrl: null,
          recordingUrl: entry.recording_s3_key ? getStreamRecordingUrl(entry.recording_s3_key) : undefined,
          query_type: entry.query_type ?? undefined,
          streamHistory: entry.result_json?.streamHistory ?? [],
          narrative: (entry.result_json?.narrative ?? []) as NarrativeEntry[],
          sessionResultId: entry.id,
          userVideoId,
          savedAt: entry.created_at,
        }
      }
      return {
        id: entry.job_id ?? entry.id,
        prompt: entry.prompt,
        answer: (entry.answer?.toUpperCase() === 'YES' ? 'YES' : 'NO') as 'YES' | 'NO',
        confidence: Math.round((entry.confidence ?? 0) * 100),
        resultText: '',
        videoThumb: entry.video_url,
        videoUrl: null,
        query_type: entry.query_type ?? undefined,
        resultPayload: entry.result_json as Record<string, unknown> | null ?? null,
        // PRI-552: restore the terminal settlement summary when the saved
        // result_json carried one (rows saved after the metering cutover).
        usage: ((entry.result_json as Record<string, unknown> | null)?.usage as ResultPill['usage']) ?? null,
        narrative: ((entry.narrative as NarrativeEntry[] | null | undefined) ??
          entry.result_json?.narrative ??
          []) as NarrativeEntry[],
        sessionResultId: entry.id,
        userVideoId,
        savedAt: entry.created_at,
      }
    })
  }

  // ── handleSelectLiveStreams ────────────────────────────────────────────────
  async function handleSelectLiveStreams() {
    trackStreamHistoryViewed()
    trackVideoLibraryTabSwitched({ tab: 'stream', is_authenticated: isAuthenticated, user_plan: userPlan })
    setSelectedSavedVideo(null)
    setSelectedDemoVideo(null)
    setSelectedFile(null)
    const allPills: ResultPill[] = []
    for (const sv of streamUserVideos) {
      try {
        const { session } = await getVideoSession(sv.id)
        const pills = sessionEntriesToPills(session, sv.id)
        allPills.push(...pills)
      } catch { /* non-fatal */ }
    }
    allPills.sort((a, b) => {
      const aTime = a.savedAt ? new Date(a.savedAt).getTime() : 0
      const bTime = b.savedAt ? new Date(b.savedAt).getTime() : 0
      return bTime - aTime
    })
    setResultPills(allPills)
    setSidebarOpen(false)
    for (const pill of allPills) {
      if (pill.recordingUrl) void attachStreamRecordingUrl(pill.id, pill.recordingUrl)
    }
  }

  // ── handleSelectUserVideo ─────────────────────────────────────────────────
  async function handleSelectUserVideo(videoId: string) {
    trackVideoLibraryTabSwitched({ tab: 'user', is_authenticated: isAuthenticated, user_plan: userPlan })
    const savedVideo = savedUserVideos.find(v => v.id === videoId)
    const [sessionResult] = await Promise.allSettled([getVideoSession(videoId)])
    if (sessionResult.status === 'fulfilled') {
      const pills = sessionEntriesToPills(sessionResult.value.session, videoId)
      setResultPills(pills)
      for (const entry of sessionResult.value.session) {
        const jobId = entry.job_id
        if (jobId && entry.query_type !== 'stream') void attachRenderedVideo(jobId)
      }
    } else {
      setResultPills([])
    }
    if (savedVideo) {
      setSelectedSavedVideo(savedVideo)
      setSelectedDemoVideo(null)
      setSelectedFile(null)
    }
    setSidebarOpen(false)
  }

  // ── handleSelectDemoVideo ─────────────────────────────────────────────────
  async function handleSelectDemoVideo(demoVideoId: string) {
    trackVideoLibraryTabSwitched({ tab: 'demo', is_authenticated: isAuthenticated, user_plan: userPlan })
    try {
      const uvId = demoVideoUserVideoIds[demoVideoId]
      if (uvId) {
        const { session } = await getVideoSession(uvId)
        setResultPills(sessionEntriesToPills(session, uvId))
        for (const entry of session) {
          if (entry.job_id) void attachRenderedVideo(entry.job_id)
        }
      } else {
        setResultPills([])
      }
    } catch (err) { console.error('[V2] Session restore failed:', err); setResultPills([]) }
    const dv = demoVideos.find(v => v.id === demoVideoId)
    if (dv) {
      setSelectedDemoVideo(demoVideoId)
      setSelectedSavedVideo(null)
      setSelectedFile(null)
      trackDemoVideoSelected({
        demo_video_id: demoVideoId,
        demo_video_name: dv.display_name,
        prompt,
        has_precomputed_result: !!demoVideoUserVideoIds[demoVideoId],
        user_plan: userPlan,
        is_authenticated: isAuthenticated,
      })
      // PRI-433 P4: /v1/demo/signal was deleted server-side (anonymous demo
      // removed) — the demo→signup funnel signal is gone with it.
    }
    setSidebarOpen(false)
  }

  // ── handleDeleteUserVideo ─────────────────────────────────────────────────
  async function handleDeleteUserVideo(videoId: string) {
    try {
      await deleteUserVideo(videoId)
      trackVideoDeleted()
      setSavedUserVideos(prev => prev.filter(v => v.id !== videoId))
      if (selectedSavedVideo?.id === videoId) {
        setSelectedSavedVideo(null)
        setResultPills([])
        setPageStateSync('idle')
      }
    } catch { /* non-fatal */ }
  }

  // ── handleDeleteDemoVideoSession ──────────────────────────────────────────
  async function handleDeleteDemoVideoSession(demoVideoId: string) {
    try {
      const uvId = demoVideoUserVideoIds[demoVideoId]
      if (uvId) {
        await deleteUserVideoSession(uvId)
        trackVideoSessionCleared()
        setDemoVideoUserVideoIds(prev => { const n = { ...prev }; delete n[demoVideoId]; return n })
      }
      if (selectedDemoVideo === demoVideoId) {
        setResultPills([])
      }
    } catch { /* non-fatal */ }
  }

  // ── handleDeleteLiveStreamSessions ────────────────────────────────────────
  async function handleDeleteLiveStreamSessions() {
    try {
      await Promise.all(streamUserVideos.map(sv => deleteUserVideo(sv.id)))
      trackVideoDeleted()
      setSavedUserVideos(prev => prev.filter(v => !streamUserVideos.some(sv => sv.id === v.id)))
      setResultPills([])
    } catch { /* non-fatal */ }
  }

  // ── handleLiveStreamsNewSession ────────────────────────────────────────────
  function handleLiveStreamsNewSession() {
    void handleSelectLiveStreams()
    setIsStreaming(true)
    lastStreamResultRef.current = null
    void requestCameraAccess()
  }

  return {
    handleSelectUserVideo,
    handleDeleteUserVideo,
    handleDeleteDemoVideoSession,
    handleDeleteLiveStreamSessions,
    handleSelectDemoVideo,
    handleSelectLiveStreams,
    handleLiveStreamsNewSession,
    sessionEntriesToPills,
  }
}
